/**
 * TeamNewsPage — News for the user's fantasy team players
 *
 * Route: /league/:id/team-news
 *
 * Filter sections:
 *   1. Frequency    (single-select) — Daily / Weekly / Monthly
 *   2. Type of News (multi-select)  — Injury / Performance / Personal
 *   3. News Source  (multi-select)  — ESPN / Shams / NBA.com / X.com
 *
 * Filter state persists to localStorage per league.
 * News is cached per the selected frequency; refreshed on demand.
 */

import { useState, useEffect, useMemo } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'

// ── Constants ──────────────────────────────────────────────────────────────────

const FREQ_OPTS = [
  { key: 'daily',   label: 'Daily',   ms: 86_400_000,    desc: 'Refreshes daily; shows today\'s news' },
  { key: 'weekly',  label: 'Weekly',  ms: 604_800_000,   desc: 'Refreshes weekly; shows last 7 days' },
  { key: 'monthly', label: 'Monthly', ms: 2_592_000_000, desc: 'Refreshes monthly; shows last 30 days' },
]

const TYPE_OPTS = [
  { key: 'injury',      label: 'Injury News',          tags: ['injury'] },
  { key: 'performance', label: 'Performance News',      tags: ['performance'] },
  { key: 'personal',    label: 'Personal Player News',  tags: ['news', 'roster'] },
]

const SOURCE_OPTS = [
  {
    key: 'espn',
    label: 'ESPN',
    match: (a) => a.source?.toLowerCase().includes('espn') && !a.byline?.toLowerCase().includes('shams'),
  },
  {
    key: 'shams',
    label: 'Shams Charania (ESPN)',
    match: (a) => a.byline?.toLowerCase().includes('shams'),
  },
  {
    key: 'nba',
    label: 'NBA.com',
    match: (a) => a.source?.toLowerCase().includes('nba.com'),
    comingSoon: true,
  },
  {
    key: 'x',
    label: 'X.com',
    match: () => false,
    comingSoon: true,
  },
]

const TAG_CONFIG = {
  injury:      { icon: '🚑', label: 'INJURY',      color: 'text-red-600',   bg: 'border-transparent px-0' },
  roster:      { icon: '🔄', label: 'ROSTER',      color: 'text-blue-700',  bg: 'border-transparent px-0' },
  performance: { icon: '⭐', label: 'PERFORMANCE', color: 'text-amber-700', bg: 'border-transparent px-0' },
  news:        { icon: '📰', label: 'NEWS',        color: 'text-gray-800',  bg: 'border-transparent px-0' },
}

const DEFAULT_FILTERS = {
  frequency: 'daily',
  types:    ['injury', 'performance', 'personal'],
  sources:  ['espn', 'shams'],
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function timeAgo(iso) {
  // Clamped at zero: ESPN's feed sometimes stamps an article a few minutes
  // in the future (clock skew), which rendered as "-48m ago" on League Home.
  const diff = Math.max(0, Date.now() - new Date(iso).getTime())
  if (isNaN(diff)) return ''
  const m = Math.floor(diff / 60_000)
  const h = Math.floor(diff / 3_600_000)
  const d = Math.floor(diff / 86_400_000)
  if (m < 1)  return 'just now'
  if (m < 60) return `${m}m ago`
  if (h < 24) return `${h}h ago`
  if (d < 30) return `${d}d ago`
  return `${Math.floor(d / 30)}mo ago`
}

function FilterIcon({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M1.5 3.5A.5.5 0 0 1 2 3h12a.5.5 0 0 1 .354.854L9.5 9.207V14.5a.5.5 0 0 1-.777.416l-2-1.333A.5.5 0 0 1 6.5 13V9.207L1.646 3.854A.5.5 0 0 1 1.5 3.5z"/>
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
      <path d="M1 4L3.5 6.5L9 1" stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function TeamNewsPage() {
  const { id }           = useParams()
  const [searchParams]   = useSearchParams()

  const [league,      setLeague]      = useState(null)
  const [articles,    setArticles]    = useState([])
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState('')
  const [lastFetched, setLastFetched] = useState(null)
  const [filtersOpen, setFiltersOpen] = useState(searchParams.get('filters') === 'open')

  // Committed filters (what's actually applied)
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(`jai_team_news_filters_${id}`)
      return saved ? { ...DEFAULT_FILTERS, ...JSON.parse(saved) } : { ...DEFAULT_FILTERS }
    } catch { return { ...DEFAULT_FILTERS } }
  })

  // Pending filters (staging area inside the drawer)
  const [pending, setPending] = useState(filters)

  useEffect(() => {
    leaguesApi.get(id).then(r => setLeague(r.data.league)).catch(() => {})
    fetchNews()
  }, [id])

  // ── Data loading ─────────────────────────────────────────────────────────────

  const fetchNews = async (force = false) => {
    const freqMs = FREQ_OPTS.find(f => f.key === filters.frequency)?.ms ?? 86_400_000

    if (!force) {
      try {
        const raw = localStorage.getItem(`jai_team_news_${id}`)
        if (raw) {
          const cached = JSON.parse(raw)
          if (Date.now() - new Date(cached.fetchedAt).getTime() < freqMs) {
            setArticles(cached.articles)
            setLastFetched(cached.fetchedAt)
            setLoading(false)
            return
          }
        }
      } catch {}
    }

    setLoading(true)
    setError('')
    try {
      const res       = await leaguesApi.getTeamNews(id)
      const arts      = res.data.articles ?? []
      const fetchedAt = res.data.fetchedAt ?? new Date().toISOString()
      setArticles(arts)
      setLastFetched(fetchedAt)
      localStorage.setItem(`jai_team_news_${id}`, JSON.stringify({ articles: arts, fetchedAt }))
    } catch (err) {
      console.error('Team news fetch failed:', err)
      if (articles.length === 0) setError('Failed to load team news. Tap ↻ to try again.')
    } finally {
      setLoading(false)
    }
  }

  // ── Filter actions ────────────────────────────────────────────────────────────

  const applyFilters = () => {
    setFilters(pending)
    localStorage.setItem(`jai_team_news_filters_${id}`, JSON.stringify(pending))
    setFiltersOpen(false)
  }

  const openFilters = () => {
    setPending(filters)   // reset pending to current committed state
    setFiltersOpen(true)
  }

  // ── Client-side filter ────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const freqOpt = FREQ_OPTS.find(f => f.key === filters.frequency)
    const cutoff  = freqOpt ? Date.now() - freqOpt.ms : 0

    return articles.filter(a => {
      // Age gate
      if (new Date(a.published).getTime() < cutoff) return false

      // Type gate
      const tagOk = filters.types.some(tk => {
        const opt = TYPE_OPTS.find(o => o.key === tk)
        return opt?.tags.includes(a.tag)
      })
      if (!tagOk) return false

      // Source gate — if only "coming soon" sources are selected, nothing passes
      const activeSources = filters.sources.filter(sk => {
        const opt = SOURCE_OPTS.find(o => o.key === sk)
        return opt && !opt.comingSoon
      })
      if (activeSources.length === 0) return false
      const srcOk = activeSources.some(sk => {
        const opt = SOURCE_OPTS.find(o => o.key === sk)
        return opt?.match(a)
      })
      if (!srcOk) return false

      return true
    })
  }, [articles, filters])

  // Count non-default active filters for badge
  const filterBadge =
    (filters.frequency !== 'daily' ? 1 : 0) +
    (filters.types.length < TYPE_OPTS.length ? 1 : 0) +
    (filters.sources.filter(s => !SOURCE_OPTS.find(o => o.key === s)?.comingSoon).length < 2 ? 1 : 0)

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-6 py-8">

        {/* Page header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <Link
              to={`/league/${id}`}
              className="text-gray-800 hover:text-gray-900 text-sm transition mb-1.5 inline-flex items-center gap-1"
            >
              ← {league?.name ?? 'League'}
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 mt-1.5">Team News</h1>
            {error ? (
              <p className="text-red-600 text-xs mt-0.5">{error}</p>
            ) : lastFetched ? (
              <p className="text-gray-800 text-xs mt-0.5">Updated {timeAgo(lastFetched)}</p>
            ) : null}
          </div>

          <div className="flex items-center gap-2 mt-1">
            <button
              onClick={() => fetchNews(true)}
              disabled={loading}
              title="Refresh"
              className="text-gray-800 hover:text-gray-900 disabled:opacity-40 transition"
            >
              <span className={`text-base ${loading ? 'animate-spin inline-block' : ''}`}>↻</span>
            </button>
            <button
              onClick={openFilters}
              className="relative flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg px-3 py-1.5 transition"
            >
              <FilterIcon />
              Filters
              {filterBadge > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-blue-700 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {filterBadge}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Active filter pills summary */}
        {(filters.types.length < TYPE_OPTS.length || filters.frequency !== 'daily') && (
          <div className="flex flex-wrap gap-2 mb-5">
            {filters.frequency !== 'daily' && (
              <span className="inline-flex items-center gap-1 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full border border-gray-300">
                {FREQ_OPTS.find(f => f.key === filters.frequency)?.label}
              </span>
            )}
            {TYPE_OPTS.filter(o => !filters.types.includes(o.key)).map(o => (
              <span key={o.key} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full line-through">
                {o.label}
              </span>
            ))}
          </div>
        )}

        {/* Articles */}
        {loading && articles.length === 0 ? (
          <NewsSkeleton />
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl px-6 py-16 text-center">
            {articles.length === 0 ? (
              <>
                <p className="text-gray-700 text-sm font-medium mb-1">No team news yet</p>
                <p className="text-gray-800 text-xs mb-4">
                  Sync your ESPN league to see news for your roster players.
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-700 text-sm font-medium mb-1">No news matches your filters</p>
                <p className="text-gray-800 text-xs mb-4">Try adjusting the frequency, type, or source.</p>
              </>
            )}
            <button
              onClick={openFilters}
              className="text-blue-700 text-sm font-medium hover:underline"
            >
              Adjust filters →
            </button>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden divide-y divide-gray-100">
            {filtered.map(a => <NewsItem key={a.id} article={a} />)}
          </div>
        )}
      </div>

      {/* Filter drawer */}
      <TeamNewsFilterDrawer
        open={filtersOpen}
        filters={pending}
        onChange={setPending}
        onApply={applyFilters}
        onReset={() => setPending({ ...DEFAULT_FILTERS })}
        onClose={() => setFiltersOpen(false)}
      />
    </AppShell>
  )
}

// ── Filter Drawer ──────────────────────────────────────────────────────────────

function TeamNewsFilterDrawer({ open, filters, onChange, onApply, onReset, onClose }) {
  const toggle = (field, key) => {
    const current = filters[field]
    const updated = current.includes(key)
      ? current.filter(k => k !== key)
      : [...current, key]
    onChange({ ...filters, [field]: updated })
  }

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/20 z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white border-l border-gray-200 z-50 flex flex-col shadow-xl transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-gray-900 font-semibold">Team News Filters</h2>
          <button
            onClick={onClose}
            className="text-gray-800 hover:text-gray-900 transition text-2xl leading-none"
            aria-label="Close filters"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-7">

          {/* Section 1 — Frequency */}
          <div>
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
              1 — Frequency
            </p>
            <p className="text-xs text-gray-800 mb-3">
              How often news refreshes and how far back content is shown.
            </p>
            <div className="flex flex-col gap-2">
              {FREQ_OPTS.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => onChange({ ...filters, frequency: opt.key })}
                  className={`flex items-start gap-3 px-3.5 py-2.5 rounded-xl text-sm text-left transition ${
                    filters.frequency === opt.key
                      ? 'bg-blue-700 text-white'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                    filters.frequency === opt.key
                      ? 'bg-white border-white'
                      : 'border-gray-300'
                  }`}>
                    {filters.frequency === opt.key && (
                      <div className="w-1.5 h-1.5 bg-blue-700 rounded-full" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium">{opt.label}</div>
                    <div className={`text-xs mt-0.5 ${filters.frequency === opt.key ? 'text-blue-200' : 'text-gray-800'}`}>
                      {opt.desc}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Section 2 — Type of News */}
          <div>
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">
              2 — Type of News
            </p>
            <div className="flex flex-col gap-2">
              {TYPE_OPTS.map(opt => {
                const active = filters.types.includes(opt.key)
                return (
                  <button
                    key={opt.key}
                    onClick={() => toggle('types', opt.key)}
                    className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-left transition ${
                      active
                        ? 'bg-blue-700 text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      active ? 'bg-white border-white' : 'border-gray-300 bg-white'
                    }`}>
                      {active && <CheckIcon />}
                    </span>
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Section 3 — News Source */}
          <div>
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">
              3 — News Source
            </p>
            <div className="flex flex-col gap-2">
              {SOURCE_OPTS.map(opt => {
                const active = filters.sources.includes(opt.key) && !opt.comingSoon
                return (
                  <button
                    key={opt.key}
                    onClick={() => !opt.comingSoon && toggle('sources', opt.key)}
                    disabled={opt.comingSoon}
                    className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-left transition ${
                      opt.comingSoon
                        ? 'bg-gray-50 text-gray-800 border border-gray-200 cursor-not-allowed opacity-60'
                        : active
                          ? 'bg-blue-700 text-white'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      active ? 'bg-white border-white' : 'border-gray-300 bg-white'
                    }`}>
                      {active && <CheckIcon />}
                    </span>
                    <span className="flex-1">{opt.label}</span>
                    {opt.comingSoon && (
                      <span className="text-xs text-gray-800 font-normal ml-auto">Soon</span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button
            onClick={onReset}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl py-2.5 text-sm transition"
          >
            Reset
          </button>
          <button
            onClick={onApply}
            className="flex-1 bg-blue-700 hover:bg-blue-500 text-white font-semibold rounded-xl py-2.5 text-sm transition"
          >
            Apply
          </button>
        </div>
      </div>
    </>
  )
}

// ── NewsItem ───────────────────────────────────────────────────────────────────

function NewsItem({ article }) {
  const tag      = TAG_CONFIG[article.tag] ?? TAG_CONFIG.news
  const citation = article.byline
    ? `${article.source} · ${article.byline}`
    : article.source

  return (
    <div className="px-5 py-4 hover:bg-gray-50 transition group">
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold border ${tag.bg} ${tag.color}`}>
          {tag.icon} {tag.label}
        </span>
        <span className="text-gray-800 text-xs">{timeAgo(article.published)}</span>
      </div>

      {article.link ? (
        <a
          href={article.link}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-gray-900 text-sm font-semibold leading-snug mb-1.5 group-hover:text-blue-700 transition line-clamp-2"
        >
          {article.headline}
        </a>
      ) : (
        <p className="text-gray-900 text-sm font-semibold leading-snug mb-1.5 line-clamp-2">
          {article.headline}
        </p>
      )}

      {article.description && (
        <p className="text-gray-700 text-xs leading-relaxed mb-2.5 line-clamp-2">
          {article.description}
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        {article.athletes?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {article.athletes.slice(0, 3).map((name, i) => (
              <span key={i} className="text-xs text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">
                {name}
              </span>
            ))}
            {article.athletes.length > 3 && (
              <span className="text-xs text-gray-800">+{article.athletes.length - 3}</span>
            )}
          </div>
        )}
        <span className="text-gray-800 text-xs flex-shrink-0 ml-auto">{citation}</span>
      </div>
    </div>
  )
}

// ── NewsSkeleton ───────────────────────────────────────────────────────────────

function NewsSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden divide-y divide-gray-100">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="px-5 py-4 animate-pulse">
          <div className="flex items-center justify-between mb-2">
            <div className="h-5 w-20 bg-gray-100 rounded-md" />
            <div className="h-3 w-12 bg-gray-100 rounded" />
          </div>
          <div className="h-4 w-3/4 bg-gray-100 rounded mb-1.5" />
          <div className="h-3 w-full bg-gray-100 rounded mb-1" />
          <div className="h-3 w-2/3 bg-gray-100 rounded mb-2.5" />
          <div className="flex justify-between">
            <div className="h-3 w-24 bg-gray-100 rounded-full" />
            <div className="h-3 w-20 bg-gray-100 rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}
